// ReadingShape — the CLOSED presentational geometry the shell dispatches on.
//
// Mirrors the editor's shell↔renderer seam (editor-general-vs-constrained.md):
// the SOURCE KIND sum is open (new formats arrive forever), but the reading
// SHAPE sum is closed — there are only so many geometries a human reads. The
// renderer registry is keyed by shape, so its count grows with geometries
// (bounded), not with formats (unbounded). See source-view-per-kind.md §2.
//
// `shapeOf` is the single TOTAL switch over the projection sum — the
// compile-forced dispatch point (exhaustSwitchCase default arm). Adding a
// projection variant, or a reading shape, must pick a mapping/renderer before
// it compiles; the graceful fallback is a deliberate routing decision, never an
// accidental blank (§4).

import { exhaustSwitchCase } from "@bilda/utils";
import type { SourceBlock, SourceProjection } from "./types.js";

// The closed reading-shape sum. The reading geometries plus the honest
// metadata-only floor `unavailable` (bytes forgotten / nothing readable).
// `paginated`, `synced`, `image`, `media`, `flat`, and `unavailable` are
// reachable today; `outlined` and `sequential` are present-but-deferred,
// gated on ingest emitting structure.
export type ReadingShape =
    | "paginated" // fixed-layout pages you scroll        (PDF)
    | "outlined" // a heading tree over flowing prose      (DOCX, Markdown, web) — deferred
    | "sequential" // ordered discrete panels              (PPTX slides) — deferred
    | "synced" // a continuous clock drives a cursor over time-addressed lines (video/audio)
    | "image" // a single still shown natively            (PNG/JPEG/… via <img>)
    | "media" // native playback, no transcript           (plain audio/video via <audio>/<video>)
    | "flat" // sectioned paragraphs, no hierarchy         (TXT; the honest fallback)
    | "unavailable"; // no readable form (metadata only)

// The single total dispatch. `pdf → paginated`, `text → flat`,
// `video → synced`, `unavailable → unavailable`. No current projection
// variant maps to `outlined`/`sequential` — those wait on the
// ingest-enrichment step that emits a heading tree / slide boundaries (§1.4,
// §3.2, §3.3).
export const shapeOf = (projection: SourceProjection): ReadingShape => {
    switch (projection.type) {
        case "pdf":
            return "paginated";
        case "text":
            return "flat";
        // A narrow reader (the right panel) reads a Word document's text;
        // the page render is the source viewer's (SourceOriginal).
        case "word":
            return "flat";
        case "video":
            return "synced";
        case "image":
            return "image";
        case "media":
            return "media";
        case "unavailable":
            return "unavailable";
        default:
            return exhaustSwitchCase(projection);
    }
};

// The blocks a projection carries. `text`/`pdf` carry reading blocks; `video`,
// `image`, `media`, and `unavailable` carry none — a video's reading structure
// is its time-addressed transcript lines, and image/plain-media bear no
// extracted text at all (they anchor at whole-file grain), not the block/
// highlight model (annotate-on-video
// is out of scope for MVP, video-source-view-synced-transcript.md §7 owner
// decision 4). This is what makes `Flat` the universal floor for the
// block-based shapes (§4 rule 1): every text-bearing projection yields at
// least paragraphs, so a renderer that can't produce richer structure
// degrades to these.
export const blocksOf = (
    projection: SourceProjection,
): readonly SourceBlock[] =>
    projection.type === "text" ||
    projection.type === "pdf" ||
    projection.type === "word"
        ? projection.blocks
        : [];

// The pure Flat projection parse — the shell↔renderer data seam built ONCE at
// the boundary. Split the one extracted-text string on blank lines into
// addressable `block-N` paragraphs (positional, edit-stable within a source);
// a block is `highlighted` exactly when a note anchors to its id. Empty /
// whitespace-only content degrades to the `unavailable` floor (§4 rule 2).
//
// This is the parse `sourceAdapter` delegates to; `httpPorts` may later upgrade
// a `text` projection to `pdf` when the original-bytes probe recognises one.
export const projectText = (
    content: string,
    anchoredIds: ReadonlySet<string>,
): SourceProjection => {
    const blocks: readonly SourceBlock[] = content
        .split(/\n\s*\n/)
        .map((paragraph) => paragraph.trim())
        .filter((paragraph) => paragraph.length > 0)
        .map((text, i): SourceBlock => {
            const id = `block-${i}`;
            return anchoredIds.has(id)
                ? { type: "highlighted", id, text }
                : { type: "plain", id, text };
        });
    return blocks.length === 0
        ? { type: "unavailable", reason: { type: "text-not-stored" } }
        : { type: "text", blocks };
};
